import React from "react";
import T from "prop-types";
import { Link } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { Authorized } from "MODULES/shared/components";
import { Search as SearchNotifications } from "MODULES/Notifications/components";
import {
  ADMIN,
  QUALITY_CONTROL,
  REPAIR,
  SALES,
  WAREHOUSE,
  WAREHOUSE_BASIC,
  QUALITY_CONTROL_ADMIN,
  QUALITY_CONTROL_MANAGER,
  ACCOUNTING,
  CAN_VIEW_ALL_INVENTORY,
  RECEIVING,
  CAN_VIEW_INFOTRADER_SETTINGS
} from "MODULES/users/constants";
import withCurrentUser, {
  WithCurrentUser
} from "MODULES/session/decorators/withCurrentUser";
import PartsCount from "MODULES/parts/components/PartsCount";
import AccountingLogs from "MODULES/AccountingLogs/Search/Search";
import QuarterlyGoals from "MODULES/QuarterlyGoals/QuarterlyGoals";
import Forbidden from "MODULES/shared/components/Authorized/Forbidden";
import TierTypeWarningMgmt from "MODULES/Admin/TierTypeWarningMgmt/TierTypeWarningMgmt";
import Settings from "MODULES/Admin/Settings/Settings";
import RemainingInventoryTotals from "MODULES/RemainingInventoryTotals/Table";
import { withRouter } from "react-router";
import { API_BASE } from "APP_ROOT/constants";
import { hasAnyOfRoles, hasPermission } from "MODULES/users/helpers";
import PartRankings from "MODULES/Admin/PartRankings/PartRankings";
import ApiKeys from "MODULES/Admin/ApiKeys/ApiKeys";
import ConsignmentReportCard from "MODULES/Admin/ConsignmentReportCard/ConsignmentReportCard";
import Integrations from "MODULES/Admin/Integrations/Integrations";
import CompanyRankings from "MODULES/Admin/CompanyRankings/CompanyRankings";
import ReportsSearch from "MODULES/Admin/Reports/Search";
import { connect } from "react-redux";
import { expire } from "MODULES/session/actions";

function Home({ currentUser, expireSession, location }) {
  const titleText =
    hasAnyOfRoles(currentUser, [REPAIR, SALES]) &&
    ![ADMIN, QUALITY_CONTROL, ACCOUNTING].includes(currentUser.role) &&
    !hasPermission(currentUser, CAN_VIEW_ALL_INVENTORY)
      ? "My Inventory"
      : "All Inventory";

  if (!currentUser) {
    expireSession();
    return null;
  }

  return (
    <Row style={{ marginTop: "30px" }}>
      <Col md={12}>
        <PartsCount />
      </Col>
      <Col md={2}>
        <div className="panel panel-default">
          <div className="panel-body">
            <ul className="nav nav-pills nav-stacked">
              <Forbidden subroles={[WAREHOUSE_BASIC]}>
                <li>
                  <Link to="/inventories">{titleText}</Link>
                </li>
              </Forbidden>
              <Authorized roles={[REPAIR, SALES]}>
                <li>
                  <Link to="/my-shopping-list">My Shopping List</Link>
                </li>
                <li>
                  <Link to="/resident-expert-parts">
                    My Resident Expert Parts
                  </Link>
                </li>
              </Authorized>
              <hr />
              <li>
                <Link to="/dashboard/receiving">Inbound Orders</Link>
              </li>
              <Authorized
                permissions={[RECEIVING]}
                roles={[QUALITY_CONTROL, REPAIR, WAREHOUSE]}
              >
                <li>
                  <Link to="/dashboard/virtual-receiving">
                    Virtual Inbound Orders
                  </Link>
                </li>
              </Authorized>
              <li>
                <Link to="/dashboard/shipping">Outbound Orders</Link>
              </li>
              <Authorized roles={[]}>
                <li>
                  <a href={`${API_BASE}/outbound_orders/download`}>
                    Download Outbound Orders
                  </a>
                </li>
              </Authorized>
              <hr />
              <Forbidden roles={[WAREHOUSE]}>
                <li>
                  <Link to="/reports">Export Retrieval</Link>
                </li>
                <li>
                  <Link to="/monthly-purchases">Monthly Purchases</Link>
                </li>
                <Authorized roles={[SALES]}>
                  <li>
                    <Link to="/purchases-report">Buyer Credit</Link>
                  </li>
                </Authorized>
                <Authorized roles={[SALES]}>
                  <li>
                    <Link to="/sales-report">Seller Credit</Link>
                  </li>
                </Authorized>
                <li>
                  <Link to="/monthly-sales">Monthly Sales</Link>
                </li>
              </Forbidden>
              <Authorized
                roles={[ADMIN, SALES, REPAIR, ACCOUNTING]}
                subroles={[[QUALITY_CONTROL_ADMIN, QUALITY_CONTROL_MANAGER]]}
              >
                <li>
                  <Link to="/monthly-depreciations">Monthly Depreciations</Link>
                </li>
                <Authorized roles={[ACCOUNTING]}>
                  {currentUser.locations &&
                    currentUser.locations.some(f => f.quickbook_realm_id) && (
                      <li>
                        <Link to="/accounting-logs">Accounting Logs</Link>
                      </li>
                    )}
                </Authorized>
              </Authorized>
              <Authorized roles={[SALES]}>
                <li>
                  <Link to="/remaining-inventory">
                    Remaining Inventory Totals
                  </Link>
                </li>
              </Authorized>
              <Forbidden roles={[WAREHOUSE]}>
                <hr />
              </Forbidden>
              <Forbidden subroles={[WAREHOUSE_BASIC]}>
                <li>
                  <Link to="/approved-vendors">Approved Vendors</Link>
                </li>
                <li>
                  <Link to="/approved-repair-stations">
                    Approved Repair Stations
                  </Link>
                </li>
                <hr />
              </Forbidden>
              <li>
                <Link to="/reports/high-scores">High Scores</Link>
              </li>
              <Authorized roles={[SALES]}>
                <li>
                  <Link to="/ranking-stats">Hot Parts</Link>
                </li>
              </Authorized>
              <li>
                <Link to="/company-ranking-stats">Top Companies</Link>
              </li>
              <Authorized>
                <li>
                  <QuarterlyGoals />
                </li>
                <li>
                  <TierTypeWarningMgmt />
                </li>
              </Authorized>
              <Authorized permissions={[CAN_VIEW_INFOTRADER_SETTINGS]}>
                <li>
                  <Settings />
                </li>
              </Authorized>
              <Authorized>
                {["development", "staging"].includes(process.env.NODE_ENV) && (
                  <li>
                    <Link to={"/integrations"}>Integrations</Link>
                  </li>
                )}
                <li>
                  <Link to={"/api-keys"}>API Keys</Link>
                </li>
              </Authorized>
            </ul>
          </div>
        </div>
      </Col>
      <Col md={9}>
        <HomeDisplays pathname={location && location.pathname} />
      </Col>
    </Row>
  );
}

const HomeDisplays = ({ pathname }) => {
  if (pathname === "/remaining-inventory") {
    return <RemainingInventoryTotals />;
  } else if (pathname === "/ranking-stats") {
    return <PartRankings />;
  } else if (pathname === "/company-ranking-stats") {
    return <CompanyRankings />;
  } else if (pathname === "/api-keys") {
    return <ApiKeys />;
  } else if (pathname === "/consignments-report") {
    return <ConsignmentReportCard />;
  } else if (pathname === "/integrations") {
    return <Integrations />;
  } else if (pathname === "/reports") {
    return <ReportsSearch />;
  } else if (pathname === "/accounting-logs") {
    return <AccountingLogs />;
  }
  return <SearchNotifications />;
};

HomeDisplays.propTypes = {
  pathname: T.string
};

export default connect(undefined, dispatch => ({
  expireSession: () => dispatch(expire())
}))(withCurrentUser(withRouter(Home)));

Home.propTypes = {
  ...WithCurrentUser
};



// WEBPACK FOOTER //
// ./app/Home.jsx